import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import {
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
} from 'reactstrap';

import AppCloseModalButton from 'components/App/AppCloseModalButton';
import Button from 'components/App/Button';
import ToogleButton from 'components/App/ToogleButton';
import INITIAL_VALUE from 'constants/initialValue';
import { PROMOTION_STATUS } from 'constants/promotion';

import styles from './ModalCreateMarketingPromotion.module.scss';
import {
  MARKETING_PROMOTION_NAME_MAX_LENGTH,
  validateMarketingPromotionName,
} from './modalValidate';

function ModalCreateMarketingPromotion(props) {
  const {
    isOpen,
    onCloseModal,
    onConfirm,
    isLoadingConfirm,
    isLoadingDetail,
    initialValues,
    editingId,
  } = props;

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isPublished, setIsPublished] = useState(false);

  const isEditing = useMemo(() => Boolean(editingId), [editingId]);

  const disabledPastDates = useCallback((current) => {
    if (!current) return false;
    return current.isBefore(dayjs().startOf('day'), 'day');
  }, []);

  const disabledEndDates = useCallback(
    (current) => {
      if (!current) return false;

      // Không cho chọn ngày < hôm nay
      if (current.isBefore(dayjs().startOf('day'), 'day')) {
        return true;
      }

      // Không cho endDate < startDate
      if (startDate && current.isBefore(startDate, 'day')) {
        return true;
      }

      return false;
    },
    [startDate],
  );

  const onClose = useCallback(() => {
    setName('');
    setNameError('');
    setStartDate(null);
    setEndDate(null);
    setIsPublished(false);
    onCloseModal();
  }, [onCloseModal]);

  useEffect(() => {
    if (!isOpen || !isEditing || !initialValues?._id) return;
    if (initialValues._id !== editingId) return;

    setName(initialValues.name || '');
    setStartDate(
      initialValues.bookingDate?.from ? dayjs(initialValues.bookingDate.from) : null,
    );
    setEndDate(
      initialValues.bookingDate?.to ? dayjs(initialValues.bookingDate.to) : null,
    );
    setIsPublished(initialValues.status === PROMOTION_STATUS.PENDING);
    setNameError('');
  }, [editingId, initialValues, isEditing, isOpen]);

  const onSubmit = useCallback(() => {
    const validateNameError = validateMarketingPromotionName(name);
    if (validateNameError) {
      setNameError(validateNameError);
      return;
    }
    const trimmedName = name.trim();

    onConfirm?.({
      name: trimmedName,
      bookingDate: {
        from: startDate?.format?.('YYYY-MM-DD') || null,
        to: endDate?.format?.('YYYY-MM-DD') || null,
      },
      status: isPublished ? PROMOTION_STATUS.PENDING : PROMOTION_STATUS.DRAFT,
    });
  }, [endDate, isPublished, name, onConfirm, startDate]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className={styles.Modal}
    >
      <div className={styles.ModalHeader}>
        <div className={styles.ModalHeaderTop}>
          <AppCloseModalButton onClick={onClose} className={styles.CloseBtn} />
        </div>

        <h3 className={styles.ModalHeaderTitle}>
          {isEditing
            ? 'Cập nhật thời gian áp dụng Ưu đãi Tiếp thị'
            : 'Thêm thời gian áp dụng Ưu đãi Tiếp thị'}
        </h3>
        <div className={styles.SectionDivider} />
      </div>

      <ModalBody className={styles.ModalBody}>
        <div className={styles.Form}>
          {isEditing && isLoadingDetail ? (
            <div className={styles.DetailLoading}>Đang tải dữ liệu...</div>
          ) : null}
          <div className={styles.Field}>
            <Label className={styles.Label}>Tên ưu đãi</Label>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError('');
              }}
              placeholder="Tên này chỉ hiển thị trong hệ thống internal"
              className={styles.Input}
              maxLength={MARKETING_PROMOTION_NAME_MAX_LENGTH}
            />
            {nameError && <div className={styles.ErrorText}>{nameError}</div>}
          </div>

          <div className={styles.Field}>
            <Label className={styles.Label}>Thời gian diễn ra ưu đãi</Label>

            <div className={styles.DateRow}>
              <DatePicker
                className={styles.DatePicker}
                value={startDate}
                onChange={setStartDate}
                placeholder="Bắt đầu vào"
                format="[Bắt đầu vào:] DD [tháng] M YYYY"
                suffixIcon={<i className={styles.DatePickerIcon} />}
                disabledDate={disabledPastDates}
              />

              <DatePicker
                className={styles.DatePicker}
                value={endDate}
                onChange={setEndDate}
                placeholder="Kết thúc vào"
                format="[Kết thúc vào:] DD [tháng] M YYYY"
                suffixIcon={<i className={styles.DatePickerIcon} />}
                disabledDate={disabledEndDates}
                defaultPickerValue={startDate || dayjs()}
              />
            </div>

            <div className={styles.PublishToggle}>
              <span className={styles.PublishToggleLabel}>
                Công khai
              </span>

              <ToogleButton
                id="marketing-promotion-publish-toggle"
                checked={isPublished}
                onChange={() => setIsPublished((prev) => !prev)}
              />
            </div>

          </div>
        </div>
      </ModalBody>

      <div className={styles.SectionDivider} />

      <ModalFooter className={styles.ModalFooter}>
        <Button
          isDefault
          classes={styles.CancelBtn}
          type="button"
          onClick={onClose}
        >
          Huỷ
        </Button>

        <Button
          classes={styles.ConfirmBtn}
          type="button"
          onClick={onSubmit}
          disabled={
            isLoadingDetail
            || !name.trim()
            || !startDate
            || !endDate
          }
          loading={isLoadingConfirm}
          name={isEditing ? 'Cập nhật' : 'Thêm'}
        >
          {isEditing ? 'Cập nhật' : 'Thêm'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

ModalCreateMarketingPromotion.propTypes = {
  isOpen: PropTypes.bool,
  isLoadingConfirm: PropTypes.bool,
  isLoadingDetail: PropTypes.bool,
  initialValues: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
    bookingDate: PropTypes.shape({
      from: PropTypes.string,
      to: PropTypes.string,
    }),
  }),
  editingId: PropTypes.string,
  onCloseModal: PropTypes.func,
  onConfirm: PropTypes.func,
};

ModalCreateMarketingPromotion.defaultProps = {
  isOpen: false,
  isLoadingConfirm: false,
  isLoadingDetail: false,
  initialValues: null,
  editingId: '',
  onCloseModal: INITIAL_VALUE.FUNCTION,
  onConfirm: INITIAL_VALUE.FUNCTION,
};

export default memo(ModalCreateMarketingPromotion);
